import { Button } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { oauthRedirectSchema } from '../../shared/oauth.ts';
import { api, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/authorize',
  component: AuthorizeAgent,
});

const scopeDescriptions: Record<string, string> = {
  'wirechunk:manage':
    'Create, read, edit, and publish sites, read form entries, and manage accounts and billing within your existing permissions.',
  openid: 'Identify your Wirechunk account.',
  profile: 'Read your name and profile.',
  email: 'Read your email address.',
  offline_access: 'Stay connected until you disconnect the agent or its authorization expires.',
};
function AuthorizeAgent() {
  const [oauthQuery] = useState(() => location.search.slice(1));
  const request = useQuery({
    queryKey: ['agent-authorization', oauthQuery],
    queryFn: async () => unwrap(await api.POST('/api/agent-authorization', { body: { oauthQuery } })),
    retry: false,
  });
  const answer = useMutation({
    mutationFn: async (accept: boolean) =>
      oauthRedirectSchema.parse(
        unwrap(await api.POST('/api/auth/oauth2/consent', { body: { accept, oauth_query: oauthQuery } })),
      ),
    onSuccess: (result) => location.assign(result.url),
  });
  if (request.isPending) return <Loading />;
  if (request.error)
    return (
      <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
        <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">
          Connection request unavailable
        </h1>
        <ErrorNotice error={request.error} />
      </main>
    );
  const info = request.data;
  return (
    <main className="mx-auto my-12 p-6 max-w-168 [&_p]:mx-0 [&_p]:my-4 [&_p]:leading-[1.65] [&_.checkbox-row]:mx-0 [&_.checkbox-row]:my-5 [&_.action-row]:mt-6 [&_.action-row]:flex-wrap">
      <p className="mx-0 mt-0 mb-3 leading-[1.65] text-[11px] font-semibold text-kumo-subtle uppercase tracking-[1.5px]">
        Connect an agent
      </p>
      <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]">
        Allow {info.name} to use Wirechunk?
      </h1>
      <p className="leading-[1.65]">This agent will act as you, within your current permissions.</p>
      {info.website ? (
        <p className="leading-[1.65]">
          Client website:{' '}
          <a href={info.website} target="_blank" rel="noreferrer">
            {info.website}
          </a>
        </p>
      ) : null}
      <ul className="mx-0 my-6 list-disc pl-5 grid gap-3">
        {info.scopes.map((scope) => (
          <li key={scope}>{scopeDescriptions[scope] ?? scope}</li>
        ))}
      </ul>
      <p className="leading-[1.65]">You can disconnect it later in Connected agents.</p>
      <ErrorNotice error={answer.error} />
      <div className="action-row flex gap-2 items-center">
        <Button variant="primary" disabled={answer.isPending} onClick={() => answer.mutate(true)}>
          Allow access
        </Button>
        <Button disabled={answer.isPending} onClick={() => answer.mutate(false)}>
          Deny
        </Button>
      </div>
    </main>
  );
}
